import React, { useCallback, useMemo, useState } from 'react';

import { Typography } from '@mui/material';
import { SegmentedControl } from 'src/components/generic/segmented-control';
import { ToggleSwitch } from 'src/components/generic/ToggleSwitch';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

type ExternalTool = Apollo.ExternalToolkitSimpleFragment['tools'][number];

const TOOL_GROUPS = [
  { heading: 'READ TOOLS', mutates: false },
  { heading: 'WRITE TOOLS', mutates: true },
] satisfies { heading: string; mutates: boolean }[];

const TOOL_VIEW = { active: 'active', all: 'all' } satisfies Record<string, string>;

const TOOL_VIEW_OPTIONS = [
  { label: 'All', value: TOOL_VIEW.all },
  { label: 'Active', value: TOOL_VIEW.active },
];

interface ExternalToolkitToolsProps {
  readonly allowedByOrg: boolean;
  readonly busy: boolean;
  readonly renderTool: (tool: ExternalTool) => JSX.Element;
  readonly toolkitName: string;
  readonly tools: readonly ExternalTool[];
  /** Distance from the scrollport top that parks a group bar under the toolkit header. */
  readonly groupStickyTop?: string;
  readonly onToggleTools?: (toolSlugs: readonly string[], enabled: boolean) => void;
}

interface ToolGroupProps {
  readonly allowedByOrg: boolean;
  readonly busy: boolean;
  readonly followsPrevious: boolean;
  readonly groupStickyTop: string;
  readonly heading: string;
  readonly isLast: boolean;
  readonly renderTool: (tool: ExternalTool) => JSX.Element;
  readonly toolkitName: string;
  readonly tools: readonly ExternalTool[];
  readonly onToggleTools?: (toolSlugs: readonly string[], enabled: boolean) => void;
}

type ToolGroupsProps = Omit<ToolGroupProps, 'followsPrevious' | 'heading' | 'isLast'>;

const useStyles = makeStyles()(theme => {
  const cardInnerRadius = `calc(${theme.spacing(1.5)} - 1px)`;
  return {
    empty: {
      color: theme.palette.text.secondary,
      padding: theme.spacing(1.5, 2),
      textAlign: 'center',
    },
    emptyLast: {
      borderBottomLeftRadius: cardInnerRadius,
      borderBottomRightRadius: cardInnerRadius,
    },
    /**
     * Later groups only. The toolkit header already draws the first rule, so a
     * top border on Read Tools would double it.
     */
    groupFollows: {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    /**
     * A banded section header rather than another row of text.
     *
     * Read and write actions carry very different consequences, and the only
     * thing separating them was a caption the same weight as the switch labels
     * under it. The tint makes the boundary a boundary; the heading says which
     * side of it you are on. Sticky so a long catalogue still says which access
     * group you are in, parked under the toolkit name.
     */
    groupHeader: {
      alignItems: 'center',
      /**
       * Opaque stand-in for `darkBackground` over white. The token is 5% alpha,
       * so tool rows showed through the bar while scrolling.
       */
      backgroundColor: 'rgb(245, 246, 246)',
      display: 'flex',
      isolation: 'isolate',
      justifyContent: 'space-between',
      padding: theme.spacing(0.5, 2),
      position: 'sticky',
      zIndex: 9,
    },
    /** Holds the title and the view control together, away from the grant switch. */
    groupHeaderLeft: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1.5),
      minWidth: 0,
    },
    groupHeading: {
      color: theme.palette.text.primary,
      flexShrink: 0,
      fontSize: 14,
      fontWeight: 700,
      letterSpacing: '0.06em',
    },
    groupToggle: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
    },
    toolList: {
      '& > :not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
    toolListLast: {
      '& > :last-child': {
        borderBottomLeftRadius: cardInnerRadius,
        borderBottomRightRadius: cardInnerRadius,
      },
    },
  };
});

/**
 * Grants or revokes every tool in this access group, not the filtered view.
 *
 * All / Active is a view. Enable all is a grant. Scoping the switch to what
 * Active left on screen made it turn on whenever you hid the tools that were
 * still off, and a click then revoked every grant instead of enabling the rest.
 */
const ToolGroupToggle = React.memo(function ToolGroupToggle({
  allowedByOrg,
  busy,
  heading,
  onToggleTools,
  tools,
}: Omit<
  ToolGroupProps,
  'followsPrevious' | 'groupStickyTop' | 'isLast' | 'renderTool' | 'toolkitName'
>): JSX.Element {
  const { classes } = useStyles();
  const enabledCount = tools.filter(tool => tool.enabled).length;
  /**
   * Two different questions. `allEnabled` is what the switch shows and what a
   * screen reader is told; `disableGroup` is which way the next click goes,
   * which a blocked toolkit forces to off even when only some tools are on.
   */
  const allEnabled = enabledCount === tools.length;
  const disableGroup = allEnabled || (!allowedByOrg && enabledCount > 0);
  const disabled = busy || !onToggleTools || (!allowedByOrg && !disableGroup);
  const handleToggle = useCallback(() => {
    if (disabled || !onToggleTools) return;
    const enabled = !disableGroup;
    onToggleTools(
      tools.filter(tool => tool.enabled !== enabled).map(tool => tool.slug),
      enabled
    );
  }, [disableGroup, disabled, onToggleTools, tools]);
  const handleKeyDown = useToggleKeyDown(handleToggle);
  return (
    <div className={classes.groupToggle}>
      {/* Not "All": the view control beside it already offers All, and this one grants. */}
      <Typography color="textSecondary" variant="body2">
        Enable all
      </Typography>
      <ToggleSwitch
        aria-checked={allEnabled}
        aria-disabled={disabled}
        aria-label={`Enable all ${heading.toLowerCase()}`}
        disabled={disabled}
        role="switch"
        tabIndex={disabled ? -1 : 0}
        toggle={handleToggle}
        toggled={allEnabled}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
});

const ToolGroup = React.memo(function ToolGroup({
  allowedByOrg,
  busy,
  followsPrevious,
  groupStickyTop,
  heading,
  isLast,
  onToggleTools,
  renderTool,
  toolkitName,
  tools,
}: ToolGroupProps): JSX.Element {
  const { classes, cx } = useStyles();
  const [enabledOnly, setEnabledOnly] = useState(false);
  const handleViewChange = useCallback(
    (view: string) => setEnabledOnly(view === TOOL_VIEW.active),
    []
  );
  const visibleTools = useMemo(
    () => (enabledOnly ? tools.filter(tool => tool.enabled) : tools),
    [enabledOnly, tools]
  );
  return (
    <section className={cx(followsPrevious && classes.groupFollows)}>
      <div className={classes.groupHeader} style={{ top: groupStickyTop }}>
        <div className={classes.groupHeaderLeft}>
          <Typography className={classes.groupHeading} component="h3" variant="body2">
            {heading}
          </Typography>
          <SegmentedControl
            ariaLabel={`Filter ${heading.toLowerCase()}`}
            options={TOOL_VIEW_OPTIONS}
            value={enabledOnly ? TOOL_VIEW.active : TOOL_VIEW.all}
            onChange={handleViewChange}
          />
        </div>
        <ToolGroupToggle
          allowedByOrg={allowedByOrg}
          busy={busy}
          heading={heading}
          tools={tools}
          onToggleTools={onToggleTools}
        />
      </div>
      {visibleTools.length === 0 && (
        <Typography className={cx(classes.empty, isLast && classes.emptyLast)} variant="body2">
          {getEmptyGroupMessage({ allowedByOrg, heading, toolkitName })}
        </Typography>
      )}
      <div className={cx(classes.toolList, isLast && classes.toolListLast)}>
        {visibleTools.map(renderTool)}
      </div>
    </section>
  );
});

const ToolGroups = React.memo(function ToolGroups({
  allowedByOrg,
  busy,
  groupStickyTop,
  onToggleTools,
  renderTool,
  toolkitName,
  tools,
}: ToolGroupsProps): JSX.Element {
  const groups = getToolGroupViews(tools);
  return (
    <>
      {groups.map((group, index) => (
        <ToolGroup
          key={group.heading}
          allowedByOrg={allowedByOrg}
          busy={busy}
          followsPrevious={index > 0}
          groupStickyTop={groupStickyTop}
          heading={group.heading}
          isLast={index === groups.length - 1}
          renderTool={renderTool}
          toolkitName={toolkitName}
          tools={group.tools}
          onToggleTools={onToggleTools}
        />
      ))}
    </>
  );
});

/**
 * A toolkit's actions, grouped into read and write sections.
 *
 * The whole catalogue renders once the connector is open. Sticky group bars
 * say which access group you are in; All / Active narrows a long list.
 */
export const ExternalToolkitTools = React.memo(function ExternalToolkitTools({
  allowedByOrg,
  busy,
  groupStickyTop = '0px',
  onToggleTools,
  renderTool,
  toolkitName,
  tools,
}: ExternalToolkitToolsProps): JSX.Element | null {
  if (tools.length === 0) return null;

  return (
    <ToolGroups
      allowedByOrg={allowedByOrg}
      busy={busy}
      groupStickyTop={groupStickyTop}
      renderTool={renderTool}
      toolkitName={toolkitName}
      tools={tools}
      onToggleTools={onToggleTools}
    />
  );
});

function useToggleKeyDown(toggle: () => void) {
  return useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      toggle();
    },
    [toggle]
  );
}

/**
 * Alphabetical within each access group, and that order does not change.
 *
 * Enabled-first made a switch jump as soon as its grant landed. The catalogue
 * is long enough that a moving row is worse than having to scan past tools
 * that are already on.
 */
function getToolGroupViews(tools: readonly ExternalTool[]) {
  const alphabetical = [...tools].sort(compareToolsByName);
  return TOOL_GROUPS.map(group => ({
    heading: group.heading,
    tools: alphabetical.filter(tool => tool.mutates === group.mutates),
  })).filter(group => group.tools.length > 0);
}

function compareToolsByName(left: ExternalTool, right: ExternalTool): number {
  const byName = left.name.localeCompare(right.name, 'en', { sensitivity: 'base' });
  if (byName !== 0) return byName;
  return left.slug.localeCompare(right.slug, 'en', { sensitivity: 'base' });
}

/**
 * Only reachable under the enabled-only filter: a group with no tools at all is
 * dropped before it renders.
 */
function getEmptyGroupMessage({
  allowedByOrg,
  heading,
  toolkitName,
}: {
  allowedByOrg: boolean;
  heading: string;
  toolkitName: string;
}): string {
  const group = heading.toLowerCase();
  if (allowedByOrg) return `No ${toolkitName} ${group} are enabled yet.`;
  return `No ${toolkitName} ${group} are enabled, and your organization has not allowed this toolkit.`;
}
